import { useState, useEffect, useMemo, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, Loader2, Check, User, Mic, Upload, X as XIcon, Target, PenLine } from "lucide-react";
import CoinFluxi from "@/assets/icons/coin-fluxi.svg";
import { cn } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { dictionary, Language } from "@/utils/dictionary";
import { s3Service } from "@/services/s3Service";
import { getVideoProductInputContext } from "@/services/ads/videos/videoDraftService";
import type { VideoStyle, VideoGenerationRequest, UGCAvatarConfig } from "../types/videoStudio.types";
import {
  AVATAR_GENDER_OPTIONS,
  AVATAR_AGE_OPTIONS,
  AVATAR_SKIN_OPTIONS,
  AVATAR_HAIR_OPTIONS,
  AVATAR_HAIR_COLOR_OPTIONS,
  AVATAR_VIBE_OPTIONS,
  AVATAR_SETTING_OPTIONS,
  PODCAST_SETTING_OPTIONS,
  creditsForStyleAndDuration,
} from "../types/videoStudio.types";
import { AUDIO_MODE, type AudioMode, type VideoProductContextResponse, type VideoProductInput } from "@/services/ads/videos/videoDraft.types";

// Phase 2 V9 REV 2 (Apr 19 2026) — feature flag. The 3-pill audio mode
// selector (Professional / Own / Fast) was removed from the sidebar because
// it forced a decision BEFORE the user saw the generated preview. The new
// flow defaults to "professional" always; voice is picked in the preview,
// and upload-your-own-voice will land in the preview in a follow-up.
// Flag kept (instead of deleting the code) so we can A/B or resurrect it
// without a full re-build — flip to `true` to show the selector again.
const SHOW_AUDIO_MODE_SELECTOR = false;

interface VideoStudioSidebarProps {
  product: VideoProductInput;
  selectedStyle: VideoStyle | null;
  onGenerate: (payload: VideoGenerationRequest) => void;
  isGenerating: boolean;

  // Phase 2 V2 — Avatar Wizard integration for modeling. The wizard lives
  // at the Step2VideoStudio level (it's a full-panel takeover), so the
  // sidebar only:
  //   · renders a preset chip when `avatar` is provided
  //   · renders a big "Elige tu presenter" CTA when it's null
  //   · bubbles a click up via `onOpenAvatarWizard` so the parent shows
  //     the wizard in place of the studio panel
  // The UGC path keeps its legacy inline avatar form (these props are
  // irrelevant there).
  avatar?: {
    config: UGCAvatarConfig;
    portraitUrl: string | null;
    displayName?: string;
  } | null;
  onOpenAvatarWizard?: () => void;
}

const COMBO_FIXED_DURATION = "30" as const;

export type VideoAngleOption = {
  id: string;
  name: string;
  description: string;
  proof?: string;
  badge?: string;
  source?: string;
};

export type ProductContextStatus = "idle" | "loading" | "ready" | "fallback";

export const getAngleBadgeLabel = (option: VideoAngleOption, index: number) => {
  const badge = option.badge?.trim();
  if (badge?.toLowerCase().includes("website")) return "Website";
  if (badge?.toLowerCase().includes("funnel")) return "Funnel";
  if (badge) return badge;
  return index === 0 ? "Base" : "Variación";
};

export function VideoStudioSidebar({
  product,
  selectedStyle,
  onGenerate,
  isGenerating,
  avatar,
  onOpenAvatarWizard,
}: VideoStudioSidebarProps) {
  const [videoTypeId, setVideoTypeId] = useState("");
  const { language } = useAuth();
  const t = (key: string, fallback?: string) =>
    dictionary[key]?.[language as Language] ?? fallback ?? key;

  // Advanced options — collapsed by default. Apple-style progressive
  // disclosure: the instruction field is optional extras, not critical
  // path, so it shouldn't compete for attention on first render.
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [userPrompt, setUserPrompt] = useState("");
  // Phase 5.4: removed problemHint state — el field se eliminó del UI porque
  // el director ya tiene product_description + sale_angle del Narrative.
  const [tone] = useState<string>("");
  const [angle, setAngle] = useState("");
  const [angleDescription, setAngleDescription] = useState("");
  const [customAngle, setCustomAngle] = useState("");
  const [productContext, setProductContext] = useState<VideoProductContextResponse | null>(null);
  const [productContextStatus, setProductContextStatus] = useState<ProductContextStatus>("idle");

  // Voice-over config (modeling only).
  // Phase 2 V3 — three audio modes (Apple-style): "professional" (default,
  // ElevenLabs curated), "own" (user uploads / records), "fast" (Kling
  // generates audio inline with the video, no TTS, no merge). This
  // replaces the older "tts | upload | none" tri-state which mixed voice
  // mode with silent-modeling, and which the user flagged as confusing
  // — every mode IS an AI-generated voice, so labeling one "Voz con IA"
  // was technically ambiguous. The three new labels describe the TRADE
  // each mode makes (quality / control / speed), not the tech underneath.
  const [audioMode, setAudioMode] = useState<AudioMode>(AUDIO_MODE.PROFESSIONAL);
  const [selectedVoiceId] = useState("Leda"); // default: Valeria (Gemini)
  const [selectedVoiceName] = useState("Valeria");

  // Uploaded audio (when audioMode === 'upload').
  // We keep both the File (for display pre-upload) and the S3 URL (set
  // after the upload completes). Upload happens on file-pick, not on
  // generate, so by the time the user clicks "Generar" the URL is ready.
  const [uploadedAudioFile, setUploadedAudioFile] = useState<File | null>(null);
  const [uploadedAudioUrl, setUploadedAudioUrl] = useState<string | null>(null);
  const [audioUploading, setAudioUploading] = useState(false);
  const [audioUploadError, setAudioUploadError] = useState<string | null>(null);
  const audioFileInputRef = useRef<HTMLInputElement | null>(null);

  // Avatar config (UGC + modeling)
  const [avatarGender, setAvatarGender] = useState<UGCAvatarConfig["gender"] | "">("");
  const [avatarAge, setAvatarAge] = useState<UGCAvatarConfig["age_range"] | "">("");
  const [avatarSkin, setAvatarSkin] = useState<UGCAvatarConfig["skin_tone"] | "">("");
  const [avatarHair, setAvatarHair] = useState<UGCAvatarConfig["hair"] | "">("");
  const [avatarHairColor, setAvatarHairColor] = useState<UGCAvatarConfig["hair_color"] | "">("");
  const [avatarVibe, setAvatarVibe] = useState<UGCAvatarConfig["vibe"] | "">("");
  const [avatarSetting, setAvatarSetting] = useState<UGCAvatarConfig["setting"] | "">("");

  const isUGC = selectedStyle?.id === "ugc-testimonial";
  // Phase 2 V2 — the gallery no longer exposes a separate "silent" card.
  // The unified Modeling card uses style_id = "product-modeling-voiceover",
  // and Step2VideoStudio resolves to "product-modeling" at submit time when
  // the user picked audioMode="none". The legacy "product-modeling" id is
  // kept here so drafts created BEFORE the unification (state in DB) still
  // render correctly under the same flags.
  const isModeling =
    selectedStyle?.id === "product-modeling-voiceover" ||
    selectedStyle?.id === "product-modeling";
  const isPodcast = selectedStyle?.id === "podcast-style";
  const isSassy = selectedStyle?.id === "sassy-object";
  const isAnimated = selectedStyle?.id === "animated-problem";
  const isCombo = isSassy || isAnimated;
  const needsAvatar = isUGC || isModeling || isPodcast;
  const shouldShowAngle = isModeling;

  // When switching styles, auto-set video_type_id (used by instruction
  // placeholder copy + analytics). For modeling we always set voiceover
  // as default — Step2VideoStudio will remap to "product-modeling" at
  // submit time if the user picked audioMode="none".
  useEffect(() => {
    if (isSassy) {
      setVideoTypeId("sassy-object");
    } else if (isAnimated) {
      setVideoTypeId("animated-problem");
    } else if (isUGC) {
      setVideoTypeId("ugc-testimonial");
    } else if (isModeling) {
      setVideoTypeId("product-modeling-voiceover");
    } else if (isPodcast) {
      setVideoTypeId("podcast-style");
      setAvatarSetting("studio-dark" as UGCAvatarConfig["setting"]);
    }
  }, [isSassy, isAnimated, isUGC, isModeling, isPodcast]);

  // Phase 2 V2 + Phase 6 V4b (Apr 20 2026) — Avatar completeness check.
  //   · Modeling + UGC now BOTH use the Avatar Wizard (prop `avatar`). User
  //     feedback: "la configuración del avatar para UGC debe ser como
  //     b-roll + voz en off" — same wizard, same UX, no more inline
  //     7-field form for UGC.
  //   · Podcast keeps the legacy inline form as a transitional fallback
  //     until its own wizard ships.
  const avatarComplete = !needsAvatar
    ? true
    : isModeling || isUGC
      ? !!avatar?.config
      : !!avatarGender && !!avatarAge && !!avatarSkin && !!avatarHair && !!avatarHairColor && !!avatarVibe && !!avatarSetting;

  // Video generation is locked to 30s in the current frontend flow.
  const effectiveDuration = COMBO_FIXED_DURATION;

  // Phase 5.1: instruction is now optional. The director Gemini can generate
  // a great script with just product name + audience + sale_angle. Forcing the
  // user to write something added friction without improving quality. If they
  // write something, great; if not, the director still works.
  // Phase 6 — UGC no requiere "tono" porque el director Gemini ya emite
  // ugc_voice_tone propio basado en el avatar y el script. Forzar al user a
  // elegir un tono además del avatar es fricción innecesaria.
  // For modeling with "Mi voz" we also require the audio to have finished
  // uploading before the generate button goes live — otherwise the draft
  // is created with audioMode="own" but no URL, and the backend has
  // nothing to merge. PROFESSIONAL and FAST don't need this gate.
  const audioComplete =
    !isModeling ||
    audioMode === AUDIO_MODE.PROFESSIONAL ||
    audioMode === AUDIO_MODE.FAST ||
    (audioMode === AUDIO_MODE.OWN && !!uploadedAudioUrl && !audioUploading);
  const angleComplete =
    !shouldShowAngle ||
    (productContextStatus !== "loading" &&
      !!angle &&
      (angle !== "Ángulo personalizado" || customAngle.trim().length > 0));

  const canGenerate =
    !!selectedStyle &&
    (isCombo || isUGC || isModeling || !!tone) &&
    avatarComplete &&
    angleComplete &&
    audioComplete &&
    !isGenerating;

  const ctaLabel = !selectedStyle
    ? t("video-studio-next")
    : !avatarComplete
      ? t("video-avatar-required-cta", "Elige un avatar")
      : !angleComplete
        ? t("video-angle-loading-cta", "Preparando ángulo")
      : !audioComplete
        ? t("video-audio-uploading", "Espera el audio")
        : t("video-studio-next");

  const footerHint = !selectedStyle
    ? null
    : !avatarComplete
      ? t("video-avatar-required-hint", "El avatar es necesario para crear las imágenes del video.")
      : t("video-generation-time");

  // Hard char limit on the user instruction so the LLM doesn't get derailed
  // by an essay and the resulting script stays within the 50-word total
  // (25 + 25) that the validators enforce.
  const INSTRUCTION_CHAR_LIMIT = 280;

  const avatarWizardEntry = (isModeling || isUGC) ? (
    <div>
      <p className="mb-2 text-[13px] font-semibold text-foreground tracking-tight">
        {isModeling ? t("video-avatar-section-modeling", "Avatar recomendado") : "Avatar"}
      </p>
      {avatar ? (
        <button
          type="button"
          onClick={onOpenAvatarWizard}
          className={cn(
            "w-full rounded-2xl bg-gradient-to-br from-white to-slate-50/50",
            "ring-1 ring-black/[0.06] hover:ring-primary/40 hover:shadow-sm",
            "p-2.5 flex items-center gap-3 text-left transition"
          )}
        >
          <div className="relative h-14 w-14 rounded-xl overflow-hidden bg-muted shrink-0 ring-1 ring-black/[0.04]">
            {avatar.portraitUrl ? (
              <img
                src={avatar.portraitUrl}
                alt={avatar.displayName ?? "Avatar"}
                className="absolute inset-0 h-full w-full object-cover"
              />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center text-muted-foreground">
                <User className="h-5 w-5" />
              </div>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[13px] font-semibold text-foreground leading-tight truncate">
              {avatar.displayName ?? t("avatar-wizard-custom-name", "Avatar personalizado")}
            </p>
            <p className="text-[11px] text-muted-foreground mt-0.5 leading-tight">
              {isModeling
                ? t("video-avatar-chip-change-modeling", "Listo para este ángulo · Cambiar")
                : t("video-avatar-chip-change", "Cambiar")}
            </p>
          </div>
          <span className="text-[14px] text-muted-foreground/70 shrink-0 pr-2">›</span>
        </button>
      ) : (
        <button
          type="button"
          onClick={onOpenAvatarWizard}
          className={cn(
            "w-full rounded-2xl bg-gradient-to-br from-indigo-50/70 to-white",
            "ring-1 ring-indigo-200/70 hover:ring-indigo-400 hover:shadow-sm",
            "p-3.5 flex items-center gap-3 text-left transition group"
          )}
        >
          <div className="h-11 w-11 rounded-xl bg-white ring-1 ring-indigo-200/60 text-indigo-600 flex items-center justify-center shrink-0 group-hover:bg-indigo-500 group-hover:text-white group-hover:ring-indigo-500 transition">
            <User className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[13px] font-semibold text-foreground leading-tight">
              {isModeling
                ? t("video-avatar-cta-title-modeling", "Elegir avatar")
                : t("video-avatar-cta-title", "Configurar avatar")}
            </p>
            <p className="text-[11px] text-muted-foreground mt-0.5 leading-tight">
              {isModeling
                ? t("video-avatar-cta-subtitle-modeling", "Sugerido según el ángulo seleccionado")
                : t("video-avatar-cta-subtitle", "Preset, personalizado o de tu elenco")}
            </p>
          </div>
          <span className="text-[14px] text-indigo-500 shrink-0 pr-2">›</span>
        </button>
      )}
    </div>
  ) : null;

  // Phase 6: el costo depende del style. UGC con Seedance 2.0 es ~3x más
  // caro que Kling V3 Pro, así que el sidebar muestra 290 para UGC y 194
  // para los otros styles a 30s.
  const totalCredits = creditsForStyleAndDuration(selectedStyle?.id, effectiveDuration);

  useEffect(() => {
    let cancelled = false;

    const loadProductContext = async () => {
      if (!shouldShowAngle) {
        setProductContext(null);
        setProductContextStatus("idle");
        setAngle("");
        setAngleDescription("");
        setCustomAngle("");
        return;
      }

      setProductContext(null);
      setProductContextStatus("loading");
      setAngle("");
      setAngleDescription("");
      setCustomAngle("");

      try {
        const response = await getVideoProductInputContext(product);
        if (cancelled) return;

        if (response.error) {
          console.warn("[VideoStudioSidebar] Product context request failed", response.error.response?.status);
        }
        const hasAngles = !!response.res?.angles?.length;
        setProductContext(hasAngles ? response.res : null);
        setProductContextStatus(hasAngles ? "ready" : "fallback");
      } catch (error) {
        if (!cancelled) {
          console.warn("[VideoStudioSidebar] Could not load product context", error);
          setProductContext(null);
          setProductContextStatus("fallback");
        }
      }
    };

    loadProductContext();
    return () => {
      cancelled = true;
    };
  }, [product, shouldShowAngle]);

  const angleOptions = useMemo(
    () => {
      if (!shouldShowAngle) {
        return [];
      }

      if (productContextStatus === "idle" || productContextStatus === "loading") {
        return [];
      }

      if (productContext?.angles?.length) {
        return productContext.angles.map((item) => ({
          id: item.id,
          name: item.name,
          description: item.description,
          proof: item.proof ?? undefined,
          badge: item.badge ?? undefined,
          source: item.source ?? undefined,
        }));
      }

      return [];
    },
    [productContext, productContextStatus, shouldShowAngle]
  );

  useEffect(() => {
    if (!shouldShowAngle) {
      setAngle("");
      setAngleDescription("");
      setCustomAngle("");
      return;
    }

    if (productContextStatus === "loading") return;
    if (angleOptions.length === 0) return;
    if (angle === "Ángulo personalizado") return;
    if (angle && angleOptions.some((option) => option.name === angle)) return;
    setAngle(angleOptions[0].name);
    setAngleDescription(angleOptions[0].description);
  }, [angle, angleOptions, productContextStatus, shouldShowAngle]);

  const handleSelectAngle = (option: VideoAngleOption) => {
    setAngle(option.name);
    setAngleDescription(option.description);
    if (option.id !== "custom") {
      setCustomAngle("");
    }
  };

  const handleCustomAngleChange = (value: string) => {
    setCustomAngle(value);
    setAngle("Ángulo personalizado");
    setAngleDescription(value);
  };

  const handleGenerate = () => {
    if (!selectedStyle) return;
    onGenerate({
      style_id: selectedStyle.id,
      video_type_id: videoTypeId,
      product_name: "",
      product_image: "",
      product_description: "",
      language: "es",
      duration: effectiveDuration as VideoGenerationRequest["duration"],
      user_prompt: isModeling ? undefined : userPrompt || undefined,
      // Phase 5.4: problem_hint eliminado del UI, ya no se llena. Lo dejamos
      // siempre en undefined para no romper el tipo de la legacy request.
      problem_hint: undefined,
      tone: tone as VideoGenerationRequest["tone"],
      angle: shouldShowAngle ? angle || undefined : undefined,
      angle_description: shouldShowAngle ? angleDescription || undefined : undefined,
      category_override: null,
      // Phase 2 V2 + Phase 6 V4b — Modeling AND UGC both pull avatar_config
      // from the wizard (prop `avatar`). Podcast keeps the legacy inline
      // form until its own wizard ships.
      avatar_config: needsAvatar
        ? isModeling || isUGC
          ? avatar?.config ?? null
          : {
            gender: avatarGender as UGCAvatarConfig["gender"],
            age_range: avatarAge as UGCAvatarConfig["age_range"],
            skin_tone: avatarSkin as UGCAvatarConfig["skin_tone"],
            hair: avatarHair as UGCAvatarConfig["hair"],
            hair_color: avatarHairColor as UGCAvatarConfig["hair_color"],
            vibe: avatarVibe as UGCAvatarConfig["vibe"],
            setting: avatarSetting as UGCAvatarConfig["setting"],
          }
        : null,
      // Phase 2 V3 — audio_mode is the new canonical switch; voice_config
      // stays as a legacy field so older backends keep working while we
      // migrate. The backend prefers audio_mode when both are present.
      audio_mode: isModeling ? audioMode : undefined,
      voice_config: isModeling
        ? {
            // Legacy backend contract mapping:
            //   professional → "tts"      (ElevenLabs voice from preview)
            //   own          → "uploaded" (user-supplied audio file)
            //   fast         → "none"     (Kling generates audio inline,
            //                              backend should skip TTS / merge)
            type:
              audioMode === AUDIO_MODE.PROFESSIONAL
                ? "tts"
                : audioMode === AUDIO_MODE.OWN
                  ? "uploaded"
                  : "none",
            voice_id: audioMode === AUDIO_MODE.PROFESSIONAL ? selectedVoiceId : undefined,
            voice_name: audioMode === AUDIO_MODE.PROFESSIONAL ? selectedVoiceName : undefined,
            provider: audioMode === AUDIO_MODE.PROFESSIONAL ? "gemini" : undefined,
            accent_city: audioMode === AUDIO_MODE.PROFESSIONAL ? "Medellín" : undefined,
            uploaded_audio_url: audioMode === AUDIO_MODE.OWN ? uploadedAudioUrl ?? undefined : undefined,
            speed: 1.0,
            stability: 0.5,
            language_code: "es",
          }
        : null,
    });
  };

  return (
    <div className="w-[40%] border-l border-indigo-100/70 flex flex-col flex-shrink-0 bg-white">
      <div className="px-6 pt-6 pb-4 flex-1 overflow-y-auto">
        {/* Sidebar title. When a style is selected we promote it to the
            header with a subtle "credits" tag on the right — gives the
            panel a clear "this is what you're configuring" framing. */}
        <div className="mb-5 flex items-start gap-3">
          <div className="flex-1 min-w-0">
            <h3 className="text-[15px] font-semibold text-foreground tracking-tight truncate">
              {selectedStyle ? t(selectedStyle.nameKey) : t("video-sidebar-title")}
            </h3>
            {!isModeling && (
              <p className="text-[12px] text-muted-foreground mt-0.5 truncate">
                {selectedStyle && selectedStyle.estimated_time
                  ? `${t("video-estimated-time")} ${selectedStyle.estimated_time}`
                  : t("video-sidebar-subtitle")}
              </p>
            )}
          </div>
          {selectedStyle && !isCombo && (
            <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-600 shrink-0">
              <img
                src={CoinFluxi}
                alt=""
                className="w-3 h-3"
                style={{ filter: "brightness(0) saturate(100%) invert(29%) sepia(96%) saturate(2293%) hue-rotate(228deg) brightness(89%) contrast(91%)" }}
              />
              <span className="text-[11px] font-semibold tabular-nums">{totalCredits}</span>
            </div>
          )}
        </div>

        {!selectedStyle ? (
          <div className="space-y-5">
            <div className="rounded-xl border-2 border-dashed border-border/40 p-5 text-center">
              <p className="text-sm text-muted-foreground/70">
                {t("video-select-style")}
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-5">
            {shouldShowAngle && (
            <div className="space-y-3">
              <div>
                <div className="min-w-0">
                  <p className="text-[13px] font-semibold text-foreground tracking-tight">
                    Ángulo del anuncio
                  </p>
                  <p className="mt-1 text-[11px] leading-snug text-muted-foreground">
                    Partimos del ángulo del website y te mostramos alternativas para el guion.
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                {productContextStatus === "loading" && (
                  <div className="space-y-2" aria-live="polite">
                    <div className="rounded-2xl border border-indigo-100 bg-gradient-to-br from-indigo-50/80 to-white px-3.5 py-3 shadow-[0_8px_24px_rgba(67,104,229,0.06)]">
                      <div className="flex items-start gap-3">
                        <div className="mt-0.5 h-8 w-8 rounded-full bg-white text-indigo-600 ring-1 ring-indigo-100 flex items-center justify-center shrink-0">
                          <Loader2 className="h-4 w-4 animate-spin" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-[13px] font-semibold text-foreground leading-snug">
                            Buscando ángulos para este producto
                          </p>
                          <p className="mt-1 text-[11px] text-muted-foreground leading-snug">
                            Revisamos el ángulo del website y preparamos variaciones coherentes antes de crear el guion.
                          </p>
                        </div>
                      </div>
                    </div>

                    {[0, 1].map((item) => (
                      <div
                        key={item}
                        className="rounded-2xl border border-indigo-100 bg-white px-3.5 py-3 shadow-[0_8px_24px_rgba(67,104,229,0.06)]"
                      >
                        <div className="flex items-start gap-3">
                          <div className="h-8 w-8 rounded-full bg-indigo-50 animate-pulse" />
                          <div className="min-w-0 flex-1 space-y-2">
                            <div className="h-3 w-4/5 rounded-full bg-indigo-100/80 animate-pulse" />
                            <div className="h-2.5 w-full rounded-full bg-slate-100 animate-pulse" />
                            <div className="h-2.5 w-2/3 rounded-full bg-slate-100 animate-pulse" />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {productContextStatus !== "loading" && angleOptions.map((option, index) => {
                  const selected = angle === option.name;
                  const badgeLabel = getAngleBadgeLabel(option, index);
                  return (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() => handleSelectAngle(option)}
                      className={cn(
                        "w-full text-left rounded-2xl border px-3.5 transition-all",
                        selected
                          ? "border-indigo-300 bg-[#F7F8FF] py-3 shadow-[0_10px_30px_rgba(67,104,229,0.10)]"
                          : "border-border/60 bg-white py-2.5 hover:border-indigo-200 hover:bg-indigo-50/40"
                      )}
                    >
                      <div className="flex items-start gap-3">
                        <div
                          className={cn(
                            "mt-0.5 h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0",
                            selected ? "bg-indigo-100 text-indigo-600" : "bg-muted/50 text-muted-foreground"
                          )}
                        >
                          <Target className="h-4 w-4" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-start gap-2">
                            <p className="min-w-0 flex-1 text-[13px] font-semibold text-foreground leading-snug">
                              {option.name}
                            </p>
                            <span className="shrink-0 rounded-full bg-indigo-50 px-2 py-0.5 text-[10px] font-semibold text-indigo-600">
                              {badgeLabel}
                            </span>
                            {selected && <Check className="mt-0.5 h-4 w-4 text-indigo-600 flex-shrink-0" />}
                          </div>
                          {selected && (
                            <>
                              <p className="mt-1.5 text-[12px] text-muted-foreground leading-snug">
                                {option.description}
                              </p>
                              {option.proof && (
                                <p className="mt-2 border-t border-indigo-100 pt-2 text-[11px] font-medium text-indigo-600/85 leading-snug">
                                  {option.proof}
                                </p>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}

                {productContextStatus === "fallback" && (
                  <p className="px-1 text-[11px] text-muted-foreground leading-snug">
                    {t("video-angle-fallback-hint", "No encontramos ángulos para este producto; puedes escribir uno propio.")}
                  </p>
                )}

                <div
                  className={cn(
                    "w-full rounded-2xl border transition-all overflow-hidden",
                    angle === "Ángulo personalizado"
                      ? "border-indigo-300 bg-gradient-to-br from-indigo-50/90 to-white shadow-[0_10px_30px_rgba(67,104,229,0.10)]"
                      : "border-dashed border-border/70 bg-white hover:border-indigo-200 hover:bg-indigo-50/30"
                  )}
                >
                  <button
                    type="button"
                    onClick={() => {
                      setAngle("Ángulo personalizado");
                      setAngleDescription(customAngle);
                    }}
                    className="w-full px-3.5 py-3 text-left"
                  >
                    <div className="flex items-start gap-3">
                      <div
                        className={cn(
                          "mt-0.5 h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0 border",
                          angle === "Ángulo personalizado"
                            ? "bg-indigo-600 text-white border-indigo-600"
                            : "bg-indigo-50 text-indigo-600 border-indigo-100"
                        )}
                      >
                        <PenLine className="h-4 w-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-start gap-2">
                          <p className="min-w-0 flex-1 text-[13px] font-semibold text-foreground leading-snug">
                            Crear ángulo propio
                          </p>
                          <span className="shrink-0 rounded-full bg-white/80 px-2 py-0.5 text-[10px] font-semibold text-indigo-600 ring-1 ring-indigo-100">
                            Personalizado
                          </span>
                          {angle === "Ángulo personalizado" && <Check className="mt-0.5 h-4 w-4 text-indigo-600 flex-shrink-0" />}
                        </div>
                        <p className="mt-1 text-[12px] text-muted-foreground leading-snug">
                          Escribe una dirección puntual si quieres salirte de las recomendaciones.
                        </p>
                      </div>
                    </div>
                  </button>

                  {angle === "Ángulo personalizado" && (
                    <div className="mx-3.5 mb-3 border-t border-indigo-100 pt-3">
                      <textarea
                        value={customAngle}
                        onChange={(event) => handleCustomAngleChange(event.target.value.slice(0, 240))}
                        placeholder="Ej: mostrar el antes y después desde una rutina real, sin sonar publicitario."
                        rows={3}
                        className="w-full rounded-xl border border-indigo-100 bg-white/90 px-3 py-2.5 text-[12px] leading-relaxed placeholder:text-muted-foreground/60 focus:border-indigo-300 outline-none transition resize-none"
                      />
                      <div className="mt-1.5 flex items-center justify-between text-[10px] text-muted-foreground/70">
                        <span>Se usará como base para el guion.</span>
                        <span className="tabular-nums">{customAngle.length}/240</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            )}

            {avatarWizardEntry}

            {/* Duration — fixed 30s while this video flow is in development.
                For B-roll + Voz en off, it lives under the title above so it
                reads as metadata for the selected format instead of another
                configuration step. */}
            {!isModeling && (
            <div>
              <p className="text-[11px] font-semibold text-indigo-600/80 uppercase tracking-wider mb-2">
                {t("video-duration")}
              </p>
              <div className="flex items-center justify-between px-3 py-2 rounded-xl bg-muted/30">
                <span className="text-[13px] font-semibold text-foreground">30s</span>
                <span className="text-[11px] text-muted-foreground">
                  {t("video-duration-fixed", "Duración estándar")}
                </span>
              </div>
            </div>
            )}

            {/* Phase 2 V9 REV 2 (Apr 19 2026) — Audio section REMOVED from
                the sidebar per user feedback. The three-pill switch
                (Professional / Mi voz / Voz rápida) added clutter to the
                sidebar and pushed the "choose audio before you even see
                the preview" decision too early. New model:
                  - audioMode defaults to "professional" for every B-roll
                    clip; the user picks the actual voice in the preview
                    via VoiceLibrary (the curated Gemini voices).
                  - "Voz rápida" (Fast, Kling inline audio) is removed
                    entirely from the UI — it produced lower-quality audio
                    and the new Gemini pipeline is fast enough that fast
                    mode stopped being worth the trade.
                  - "Mi voz" (user upload) is moving to the preview panel
                    in a follow-up so the user can opt-in AFTER seeing the
                    generated script (requires a new backend endpoint to
                    patch audio_mode + audio_url on the draft).
                The full audio mode block used to live here. See git
                history if you need to resurrect it.
                Kept below: only the fixed duration display and the
                advanced-options collapsible. */}
            {SHOW_AUDIO_MODE_SELECTOR && isModeling && (
              <div>
                <p className="text-[11px] font-semibold text-indigo-600/80 uppercase tracking-wider mb-2">
                  {t("video-audio-label", "Audio")}
                </p>
                <div
                  role="radiogroup"
                  aria-label={t("video-audio-label", "Audio")}
                  className="grid grid-cols-3 gap-1 rounded-xl border border-indigo-200/60 bg-indigo-50/30 p-1"
                >
                  {([
                    {
                      value: AUDIO_MODE.PROFESSIONAL,
                      labelKey: "video-audio-mode-professional",
                      fallback: "Voz profesional",
                      hintKey: "video-audio-mode-professional-hint",
                      hintFallback: "8 voces colombianas + ajustes",
                      recommended: true,
                    },
                    {
                      value: AUDIO_MODE.OWN,
                      labelKey: "video-audio-mode-own",
                      fallback: "Mi voz",
                      hintKey: "video-audio-mode-own-hint",
                      hintFallback: "Sube tu propia grabación",
                    },
                    {
                      value: AUDIO_MODE.FAST,
                      labelKey: "video-audio-mode-fast",
                      fallback: "Voz rápida",
                      hintKey: "video-audio-mode-fast-hint",
                      hintFallback: "Generada al vuelo con el video",
                    },
                  ]).map((opt) => {
                    const active = audioMode === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        role="radio"
                        aria-checked={active}
                        onClick={() => setAudioMode(opt.value)}
                        title={t(opt.hintKey, opt.hintFallback)}
                        className={cn(
                          "relative py-1.5 px-1 rounded-lg text-[11px] font-semibold transition-all",
                          active
                            ? "bg-white text-indigo-700 shadow-sm ring-1 ring-indigo-100"
                            : "text-muted-foreground hover:text-foreground"
                        )}
                      >
                        {opt.recommended && !active && (
                          <span className="absolute -top-1 -right-1 h-1.5 w-1.5 rounded-full bg-indigo-500" aria-hidden="true" />
                        )}
                        {t(opt.labelKey, opt.fallback)}
                      </button>
                    );
                  })}
                </div>

                {/* Short description of the currently selected mode — keeps
                    the three-pill row tight but still informs the user of
                    what each choice actually does. */}
                <p className="mt-1.5 text-[11px] text-muted-foreground leading-snug">
                  {audioMode === AUDIO_MODE.PROFESSIONAL
                    ? t("video-audio-mode-professional-desc", "Elige una voz colombiana en el preview y ajusta velocidad y emoción. Recomendado.")
                    : audioMode === AUDIO_MODE.OWN
                      ? t("video-audio-mode-own-desc", "Sube un MP3 con tu voz (máx 15s). Nosotros lo sincronizamos con el video.")
                      : t("video-audio-mode-fast-desc", "Más rápido: el video y la voz se generan en una sola pasada. Menos control sobre la voz.")}
                </p>

                {audioMode === AUDIO_MODE.OWN && (
                  <div className="mt-2">
                    <input
                      ref={audioFileInputRef}
                      type="file"
                      accept="audio/mpeg,audio/mp3,audio/wav,audio/x-wav,.mp3,.wav"
                      className="hidden"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        setUploadedAudioFile(file);
                        setAudioUploadError(null);
                        setUploadedAudioUrl(null);
                        setAudioUploading(true);
                        try {
                          const url = await s3Service.uploadImage(file);
                          setUploadedAudioUrl(url);
                        } catch {
                          setAudioUploadError(
                            t("video-audio-upload-error", "No pudimos subir el audio. Intentá de nuevo.")
                          );
                          setUploadedAudioFile(null);
                        } finally {
                          setAudioUploading(false);
                          if (audioFileInputRef.current) audioFileInputRef.current.value = "";
                        }
                      }}
                    />
                    {!uploadedAudioFile ? (
                      <button
                        type="button"
                        onClick={() => audioFileInputRef.current?.click()}
                        className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg border border-dashed border-indigo-200 bg-white hover:border-indigo-400 hover:bg-indigo-50/40 text-left transition"
                      >
                        <Upload className="h-3.5 w-3.5 text-indigo-500 shrink-0" />
                        <span className="text-[12px] font-medium text-foreground/80">
                          {t("video-audio-upload-pick", "Sube tu audio (MP3 / WAV)")}
                        </span>
                      </button>
                    ) : (
                      <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white border border-indigo-200">
                        {audioUploading ? (
                          <Loader2 className="h-3.5 w-3.5 text-indigo-500 animate-spin shrink-0" />
                        ) : uploadedAudioUrl ? (
                          <Check className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
                        ) : (
                          <Mic className="h-3.5 w-3.5 text-indigo-500 shrink-0" />
                        )}
                        <p className="text-[12px] font-medium text-foreground truncate flex-1">
                          {uploadedAudioFile.name}
                        </p>
                        <button
                          type="button"
                          onClick={() => {
                            setUploadedAudioFile(null);
                            setUploadedAudioUrl(null);
                            setAudioUploadError(null);
                          }}
                          aria-label={t("video-audio-upload-remove", "Quitar audio")}
                          className="h-6 w-6 rounded-md flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted/50 transition shrink-0"
                        >
                          <XIcon className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    )}
                    {audioUploadError && (
                      <p className="mt-1.5 text-[11px] text-red-500 leading-snug">
                        {audioUploadError}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Avatar Config — Podcast ONLY (Modeling + UGC both use the
                wizard above). This inline 7-field form stays as a
                transitional fallback until Podcast gets its own wizard. */}
            {needsAvatar && !isModeling && !isUGC && (
              <div className="rounded-xl border-2 border-indigo-200 bg-white shadow-sm shadow-indigo-100/25 overflow-hidden">
                <div className="flex items-center gap-1.5 px-3 py-2.5 bg-indigo-50/60 border-b border-indigo-100">
                  <User className="h-3.5 w-3.5 text-indigo-500" />
                  <span className="text-xs font-semibold text-indigo-700">{t("video-avatar-title")}</span>
                </div>

                <div className="px-3 pb-3.5 pt-3 space-y-3">
                  {/* Gender */}
                  <AvatarButtonGroup
                    label={t("video-avatar-gender")}
                    options={AVATAR_GENDER_OPTIONS}
                    value={avatarGender ?? ""}
                    onChange={(v) => setAvatarGender(v as UGCAvatarConfig["gender"])}
                    t={t}
                  />

                  {/* Age */}
                  <AvatarButtonGroup
                    label={t("video-avatar-age")}
                    options={AVATAR_AGE_OPTIONS}
                    value={avatarAge ?? ""}
                    onChange={(v) => setAvatarAge(v as UGCAvatarConfig["age_range"])}
                    t={t}
                  />

                  {/* Skin tone */}
                  <div>
                    <p className="text-[10px] font-semibold text-indigo-600/80 mb-1.5 uppercase tracking-wider">
                      {t("video-avatar-skin")}
                    </p>
                    <div className="flex gap-2">
                      {AVATAR_SKIN_OPTIONS.map((opt) => (
                        <button
                          key={opt.value}
                          onClick={() => setAvatarSkin(opt.value as UGCAvatarConfig["skin_tone"])}
                          className={cn(
                            "flex flex-col items-center gap-1 px-2 py-1.5 rounded-lg border-2 transition-all flex-1",
                            avatarSkin === opt.value
                              ? "border-indigo-500 bg-indigo-50"
                              : "border-border/60 hover:border-foreground/20"
                          )}
                        >
                          <div
                            className="w-6 h-6 rounded-full border border-black/10"
                            style={{ backgroundColor: opt.color }}
                          />
                          <span className="text-[8px] font-medium text-muted-foreground">{t(opt.labelKey)}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Hair */}
                  <AvatarButtonGroup
                    label={t("video-avatar-hair")}
                    options={AVATAR_HAIR_OPTIONS}
                    value={avatarHair ?? ""}
                    onChange={(v) => setAvatarHair(v as UGCAvatarConfig["hair"])}
                    t={t}
                    columns={3}
                  />

                  {/* Hair color */}
                  <div>
                    <p className="text-[10px] font-semibold text-indigo-600/80 mb-1.5 uppercase tracking-wider">
                      {t("video-avatar-hair-color")}
                    </p>
                    <div className="flex gap-2">
                      {AVATAR_HAIR_COLOR_OPTIONS.map((opt) => (
                        <button
                          key={opt.value}
                          onClick={() => setAvatarHairColor(opt.value as UGCAvatarConfig["hair_color"])}
                          className={cn(
                            "flex flex-col items-center gap-1 px-2 py-1.5 rounded-lg border-2 transition-all flex-1",
                            avatarHairColor === opt.value
                              ? "border-indigo-500 bg-indigo-50"
                              : "border-border/60 hover:border-foreground/20"
                          )}
                        >
                          <div
                            className="w-6 h-6 rounded-full border border-black/10"
                            style={{ backgroundColor: opt.color }}
                          />
                          <span className="text-[8px] font-medium text-muted-foreground">{t(opt.labelKey)}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Vibe */}
                  <AvatarButtonGroup
                    label={t("video-avatar-vibe")}
                    options={AVATAR_VIBE_OPTIONS}
                    value={avatarVibe ?? ""}
                    onChange={(v) => setAvatarVibe(v as UGCAvatarConfig["vibe"])}
                    t={t}
                  />

                  {/* Setting */}
                  <AvatarButtonGroup
                    label={t("video-avatar-setting")}
                    options={isPodcast ? PODCAST_SETTING_OPTIONS : AVATAR_SETTING_OPTIONS}
                    value={avatarSetting ?? ""}
                    onChange={(v) => setAvatarSetting(v as UGCAvatarConfig["setting"])}
                    t={t}
                    columns={3}
                  />
                </div>
              </div>
            )}

            {/* Advanced Options — combo keeps the instruction visible because
                it is the primary creative lever. B-roll now uses the selected
                angle as the script driver, so the general instruction field is
                hidden unless the user explicitly creates a custom angle. */}
            {isCombo ? (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-[11px] font-semibold text-muted-foreground">
                    {t("video-instruction")}{" "}
                    <span className="font-normal text-muted-foreground/60">
                      ({t("video-optional")})
                    </span>
                  </p>
                  <span
                    className={cn(
                      "text-[10px] tabular-nums",
                      userPrompt.length > INSTRUCTION_CHAR_LIMIT * 0.9
                        ? "text-amber-600 font-semibold"
                        : "text-muted-foreground/60"
                    )}
                  >
                    {userPrompt.length}/{INSTRUCTION_CHAR_LIMIT}
                  </span>
                </div>
                <textarea
                  value={userPrompt}
                  onChange={(e) => {
                    const next = e.target.value;
                    if (next.length <= INSTRUCTION_CHAR_LIMIT) {
                      setUserPrompt(next);
                    }
                  }}
                  maxLength={INSTRUCTION_CHAR_LIMIT}
                  placeholder={t(getInstructionPlaceholderKey(isUGC, isPodcast, videoTypeId))}
                  rows={4}
                  className="w-full rounded-xl bg-muted/30 border border-transparent px-3 py-2.5 text-[12px] placeholder:text-muted-foreground/60 focus:border-primary/40 focus:bg-white outline-none transition resize-none"
                />
              </div>
            ) : !isModeling ? (
            <div>
              <button
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="w-full flex items-center justify-between py-2 text-[12px] font-medium text-muted-foreground hover:text-indigo-600 transition-colors"
              >
                <span>{t("video-advanced-options")}</span>
                <ChevronDown
                  className={cn(
                    "h-3.5 w-3.5 transition-transform duration-200",
                    showAdvanced && "rotate-180"
                  )}
                />
              </button>

              <AnimatePresence>
                {showAdvanced && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.2 }}
                    className="overflow-hidden"
                  >
                    <div className="pt-2 space-y-3">
                      {/* User Prompt — optional, capped char limit.
                          Phase 2 V9 REV 2e — label consistent with the
                          isCombo branch: "Instrucción (opcional)" + char
                          counter on the right. No separate helper para —
                          the textarea placeholder carries the guidance. */}
                      <div>
                        <div className="flex items-center justify-between mb-1.5">
                          <p className="text-[11px] font-medium text-muted-foreground">
                            {t("video-instruction")}{" "}
                            <span className="font-normal text-muted-foreground/60">
                              ({t("video-optional")})
                            </span>
                          </p>
                          <span
                            className={cn(
                              "text-[10px] tabular-nums",
                              userPrompt.length > INSTRUCTION_CHAR_LIMIT * 0.9
                                ? "text-amber-600 font-semibold"
                                : "text-muted-foreground/60"
                            )}
                          >
                            {userPrompt.length}/{INSTRUCTION_CHAR_LIMIT}
                          </span>
                        </div>
                        <textarea
                          value={userPrompt}
                          onChange={(e) => {
                            const next = e.target.value;
                            if (next.length <= INSTRUCTION_CHAR_LIMIT) {
                              setUserPrompt(next);
                            }
                          }}
                          maxLength={INSTRUCTION_CHAR_LIMIT}
                          placeholder={t(getInstructionPlaceholderKey(isUGC, isPodcast, videoTypeId))}
                          rows={3}
                          className="w-full rounded-xl bg-muted/30 border border-transparent px-3 py-2 text-[12px] placeholder:text-muted-foreground/60 focus:border-primary/40 focus:bg-white outline-none transition resize-none"
                        />
                      </div>

                      {/* Phase 5.4: el field "¿Qué problema resuelve?" se eliminó.
                          El director ya tiene product_description + sale_angle del Narrative,
                          que cubren ese contexto. Pedirlo de nuevo era redundante.

                          Phase 2 V2: el Tone autocomplete (auto / educational
                          / dramatic / humorous / urgent) también se removió.
                          El director define tono, ritmo y decisiones visuales
                          dentro del plan completo, así que pedir un tono aparte
                          lo pisa y confunde al usuario sin aportar calidad medible. */}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            ) : null}
          </div>
        )}
      </div>

      {/* Footer — compact, one-line CTA with credits inline. Indigo-tinted
          top border ties the footer back to the brand strip on top. */}
      <div className="px-5 py-4 border-t border-indigo-100/70 bg-gradient-to-b from-indigo-50/20 to-white flex-shrink-0">
        <button
          type="button"
          onClick={handleGenerate}
          disabled={!canGenerate}
          className={cn(
            "w-full flex items-center justify-center gap-2.5 h-11 text-[14px] font-semibold rounded-xl text-white",
            "shadow-[0_2px_8px_rgba(67,104,229,0.25)]",
            "hover:shadow-[0_4px_12px_rgba(67,104,229,0.35)] active:shadow-[0_1px_4px_rgba(67,104,229,0.2)]",
            "transition-all",
            "disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none"
          )}
          style={{ background: "linear-gradient(135deg, #4368E5 0%, #5B7BF0 100%)" }}
        >
          {isGenerating ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              {t("video-studio-preparing")}
            </>
          ) : (
            // Phase 2 V2 — the sidebar CTA never charges credits by itself.
            // Clicking "Continuar" dispatches the director (script only) and
            // lands the user in the preview, where the REAL cobro happens
            // on "Crear mi video". Calling this button "Generar Video" was
            // misleading — implied that the render had already started.
            <span>{ctaLabel}</span>
          )}
        </button>
        {footerHint && !isGenerating && (
          <p className="text-[10px] text-muted-foreground/70 text-center mt-1.5">
            {footerHint}
          </p>
        )}
      </div>
    </div>
  );
}

function getInstructionPlaceholderKey(isUGC: boolean, isPodcast: boolean, videoTypeId: string): string {
  if (isUGC) return "video-instruction-placeholder-ugc";
  if (isPodcast) return "video-instruction-placeholder-podcast";
  // Phase 2 V9 REV 2 — B-roll + Voz en off needs its own placeholder. The
  // old code fell through to the sassy-object example ("producto furioso")
  // which was misleading. Now the hint targets narrative levers the director
  // actually responds to: ingredient to highlight, tone, audience, angle.
  if (videoTypeId === "product-modeling-voiceover") return "video-instruction-placeholder-modeling-voiceover";
  if (videoTypeId === "product-modeling") return "video-instruction-placeholder-modeling";
  if (videoTypeId === "animated-problem") return "video-instruction-placeholder-animated-problem";
  return "video-instruction-placeholder-sassy-object";
}

/** Reusable button group for avatar options */
function AvatarButtonGroup({
  label,
  options,
  value,
  onChange,
  t,
  columns = 2,
}: {
  label: string;
  options: readonly { value: string; labelKey: string }[];
  value: string;
  onChange: (v: string) => void;
  t: (key: string, fallback?: string) => string;
  columns?: number;
}) {
  return (
    <div>
      <p className="text-[10px] font-semibold text-indigo-600/80 mb-1.5 uppercase tracking-wider">
        {label}
      </p>
      <div className={cn(
        "grid gap-1.5",
        columns === 2 && "grid-cols-2",
        columns === 3 && "grid-cols-3",
        columns === 4 && "grid-cols-4"
      )}>
        {options.map((opt) => (
          <button
            key={opt.value}
            onClick={() => onChange(opt.value)}
            className={cn(
              "px-2 py-1.5 rounded-lg border-2 text-[10px] font-medium transition-all text-center",
              value === opt.value
                ? "border-indigo-500 bg-indigo-50 text-indigo-700"
                : "border-border/60 text-muted-foreground hover:border-foreground/20"
            )}
          >
            {t(opt.labelKey)}
          </button>
        ))}
      </div>
    </div>
  );
}
